<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

// `stage` null = criar; objeto = editar. `labels` = etiquetas da conta (chips).
const props = defineProps({
  stage: { type: Object, default: null },
  labels: { type: Array, default: () => [] },
});
const emit = defineEmits(['submit']);
const { t } = useI18n();

const dialogRef = ref(null);

const emptyForm = () => ({
  name: '',
  label_ids: [],
  behavior: 'open',
  wip_limit: '',
  rotting_days: '',
  probability: '',
});
const form = reactive(emptyForm());

const isEdit = computed(() => Boolean(props.stage?.id));
const isInvalid = computed(() => !form.name.trim());

// Comportamento como controle segmentado iconográfico (mais claro que um <select>).
const behaviorOptions = computed(() => [
  {
    value: 'open',
    label: t('PIPELINES.STAGES.BEHAVIOR_OPEN'),
    icon: 'i-lucide-circle-dot',
    activeClass: 'bg-n-alpha-2 text-n-slate-12 outline-n-slate-7',
  },
  {
    value: 'won',
    label: t('PIPELINES.STAGES.BEHAVIOR_WON'),
    icon: 'i-lucide-trophy',
    activeClass: 'bg-n-teal-3 text-n-teal-11 outline-n-teal-7',
  },
  {
    value: 'lost',
    label: t('PIPELINES.STAGES.BEHAVIOR_LOST'),
    icon: 'i-lucide-x-circle',
    activeClass: 'bg-n-ruby-3 text-n-ruby-11 outline-n-ruby-7',
  },
]);

// Campo opcional → inteiro >= 0 (ou null). Lixo/negativo vira null.
const toOptionalInt = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return parsed;
};
// probability 0-100.
const toProbability = value => {
  const parsed = toOptionalInt(value);
  return parsed === null ? null : Math.min(parsed, 100);
};

const hydrate = () => {
  Object.assign(form, emptyForm());
  if (props.stage) {
    Object.assign(form, {
      name: props.stage.name ?? '',
      label_ids: [...(props.stage.label_ids || [])],
      behavior: props.stage.behavior ?? 'open',
      wip_limit: props.stage.wip_limit ?? '',
      rotting_days: props.stage.rotting_days ?? '',
      probability: props.stage.probability ?? '',
    });
  }
};

const open = () => {
  hydrate();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const toggleLabel = id => {
  const idx = form.label_ids.indexOf(id);
  if (idx === -1) form.label_ids.push(id);
  else form.label_ids.splice(idx, 1);
};

const handleConfirm = () => {
  if (isInvalid.value) return;
  emit('submit', {
    id: props.stage?.id ?? null,
    name: form.name.trim(),
    label_ids: [...form.label_ids],
    behavior: form.behavior,
    wip_limit: toOptionalInt(form.wip_limit),
    rotting_days: toOptionalInt(form.rotting_days),
    probability: toProbability(form.probability),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :confirm-button-label="
      isEdit ? t('PIPELINES.STAGES.SAVE') : t('PIPELINES.STAGES.ADD')
    "
    :disable-confirm-button="isInvalid"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-5">
      <header class="flex gap-3 items-center">
        <span
          class="grid flex-shrink-0 place-items-center rounded-xl size-10 bg-n-alpha-2 text-n-brand"
        >
          <span class="size-5 i-lucide-columns-3" />
        </span>
        <div class="flex flex-col min-w-0">
          <h3 class="text-base font-medium text-n-slate-12">
            {{
              isEdit
                ? t('PIPELINES.STAGES.EDIT')
                : t('PIPELINES.STAGES.CREATE_TITLE')
            }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('PIPELINES.STAGES.SUBTITLE') }}
          </p>
        </div>
      </header>

      <Input
        v-model="form.name"
        autofocus
        :label="t('PIPELINES.STAGES.NAME')"
        :placeholder="t('PIPELINES.STAGES.NAME_PLACEHOLDER')"
      />

      <!-- Etiquetas (chips) -->
      <div class="flex flex-col gap-1.5">
        <span
          class="flex gap-1.5 items-center text-sm font-medium text-n-slate-12"
        >
          <span class="size-4 i-lucide-tags text-n-blue-10" />
          {{ t('PIPELINES.STAGES.LABELS') }}
        </span>
        <div v-if="labels.length" class="flex flex-wrap gap-2">
          <button
            v-for="label in labels"
            :key="label.id"
            type="button"
            class="px-2 py-1 text-xs rounded-md outline outline-1 transition-colors"
            :class="
              form.label_ids.includes(label.id)
                ? 'text-white outline-transparent'
                : 'text-n-slate-11 outline-n-weak hover:bg-n-alpha-1'
            "
            :style="
              form.label_ids.includes(label.id)
                ? { backgroundColor: label.color }
                : {}
            "
            @click="toggleLabel(label.id)"
          >
            {{ label.title }}
          </button>
        </div>
        <p v-if="!labels.length" class="text-xs text-n-slate-10">
          {{ t('PIPELINES.STAGES.NO_LABELS') }}
        </p>
        <p class="text-xs leading-snug text-n-slate-10">
          {{ t('PIPELINES.STAGES.LABELS_HINT') }}
        </p>
      </div>

      <!-- Comportamento (segmentado iconográfico) -->
      <div class="flex flex-col gap-1.5">
        <span
          class="flex gap-1.5 items-center text-sm font-medium text-n-slate-12"
        >
          <span class="size-4 i-lucide-flag text-n-iris-10" />
          {{ t('PIPELINES.STAGES.BEHAVIOR') }}
        </span>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="option in behaviorOptions"
            :key="option.value"
            type="button"
            class="flex flex-col gap-1 items-center px-2 py-2.5 text-xs font-medium rounded-lg outline outline-1 transition-colors"
            :class="
              form.behavior === option.value
                ? option.activeClass
                : 'text-n-slate-11 outline-n-weak hover:bg-n-alpha-1'
            "
            @click="form.behavior = option.value"
          >
            <span class="size-4" :class="option.icon" />
            {{ option.label }}
          </button>
        </div>
        <p class="text-xs leading-snug text-n-slate-10">
          {{ t('PIPELINES.STAGES.BEHAVIOR_HINT') }}
        </p>
      </div>

      <!-- Config avançada: WIP / apodrecimento / probabilidade. Labels com altura uniforme
           (min-h) p/ os Inputs alinharem entre as colunas mesmo com rótulos de 1 ou 2 linhas. -->
      <div class="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
        <div class="flex flex-col gap-1.5">
          <span
            class="flex gap-1.5 items-start text-xs font-medium min-h-9 text-n-slate-11"
          >
            <span
              class="mt-0.5 size-3.5 i-lucide-gauge text-n-amber-10 shrink-0"
            />
            {{ t('PIPELINES.STAGES.WIP_LIMIT') }}
          </span>
          <Input
            v-model="form.wip_limit"
            type="number"
            min="0"
            :placeholder="t('PIPELINES.STAGES.OPTIONAL')"
          />
          <p class="text-[11px] leading-snug text-n-slate-10">
            {{ t('PIPELINES.STAGES.WIP_HINT') }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            class="flex gap-1.5 items-start text-xs font-medium min-h-9 text-n-slate-11"
          >
            <span
              class="mt-0.5 size-3.5 i-lucide-flame text-n-ruby-10 shrink-0"
            />
            {{ t('PIPELINES.STAGES.ROTTING_DAYS') }}
          </span>
          <Input
            v-model="form.rotting_days"
            type="number"
            min="0"
            :placeholder="t('PIPELINES.STAGES.OPTIONAL')"
          />
          <p class="text-[11px] leading-snug text-n-slate-10">
            {{ t('PIPELINES.STAGES.ROTTING_HINT') }}
          </p>
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            class="flex gap-1.5 items-start text-xs font-medium min-h-9 text-n-slate-11"
          >
            <span
              class="mt-0.5 size-3.5 i-lucide-percent text-n-teal-10 shrink-0"
            />
            {{ t('PIPELINES.STAGES.PROBABILITY') }}
          </span>
          <Input
            v-model="form.probability"
            type="number"
            min="0"
            max="100"
            :placeholder="t('PIPELINES.STAGES.OPTIONAL')"
          />
          <p class="text-[11px] leading-snug text-n-slate-10">
            {{ t('PIPELINES.STAGES.PROBABILITY_HINT') }}
          </p>
        </div>
      </div>
    </div>
  </Dialog>
</template>
